<!--
  canonical product editor Stage — persistent Artifi preview surface.

  Plan: 02-product-editor.md (canonical product editor).

  Day 2 (this version) + PR-N2 (resolver rewire) + Tier 1 activePreview:
    - Reactive args computed reads:
        * productEditor.serverState.data.attributes.product_item_number
        * productEditorUiState.selectedColorSku, selectedView,
          effectivePlacement (the selection, else the FIRST placement,
          else null -- design-philosophy principle 4: the rendered view
          and the rendered artwork derive from this ONE value)
      and assembles {productSku, colorSku, viewCode, artworkFileId,
      variantSku, variantBindings} for useArtifiPreviewCache.
      variantBindings carries BOTH what each variant puts on the
      garment and which colorway it sews, so rebinding a colorway
      invalidates the preview (see the computed's own comment).
    - PR-N2: routes the active variant via useVariantResolver
      (specific_colors > catch_all L/D > single_catch_all, with
      manual_ld override folded in). The legacy variantRoutingMap
      (colorSku -> variant_key letter) is gone; per-color overrides
      now flow through manual_ld.
    - With no selection, the Stage previews the FIRST placement on its
      own zone (never another placement's artwork on a defaulted view);
      only a product with NO placements renders bare.
    - When the selected placement has no bound artwork on the routed
      variant, renders an SVG dashed zone overlay positioned via
      catalog-driven box from usePlacementZoneBoxes (Phase F F3.c) --
      sized to the product photo's actual pixel dimensions so the
      overlay lines up with the rendered garment image. Falls back to
      hiding the overlay when the catalog has no row for the (SKU,
      view) pair, rather than fabricating a synthetic position (the
      bug F3.c fixes).

  Reuses the spiritual logic of LivePreviewCard.vue but in the new
  position (top of main column, full-width, not a right-aside card).
-->
<template>
    <div
        data-testid="stage-root"
        class="stage-root"
        :class="{ 'stage-root--content-sized': fallbackOptionsShown }"
    >
        <!--
          Upload your own image for the selected color: one slim row, offered
          whether or not Artifi can render it, and whatever the Zone diagram /
          Open canvas switch says (Jake, 2026-10-28). It sits ABOVE the
          preview so it is on screen whatever height the preview takes (the
          fallback tile grows with its content and pushed a bottom row below
          the fold at 1280x720).
        -->
        <ColorImageStrip
            v-if="showColorImageStrip"
            :color-label="fallbackProps.colorLabel"
            :blank-url="fallbackProps.blankUrl"
            :custom-preview-url="fallbackProps.customPreviewUrl"
            :status="fallbackProps.status"
            @upload="onFallbackUpload"
            @remove="onFallbackRemove"
        />
        <div class="stage-preview-area">
            <!--
              Preview precedence for a DST/EMB placement (corrected
              2026-08-03, product decision by Jake). The active variant may
              have BOTH:
                * an Artifi COMPOSITE (product mockup with the decoration
                  composited onto the garment at the placement zone),
                  surfaced by useArtifiPreviewCache as ``previewUrl``, and
                * a raw stitch-file render -- the flat Wilcom Trueview raster
                  on a TRANSPARENT background, produced by the artwork_preview
                  lazy-render machine (PR-4) and carried on the active variant
                  as ``preview = {state, preview_url}``.
              The Artifi composite is the real on-garment preview and WINS
              when available. The raw stitch render (``dstPreview``) is only a
              FALLBACK for when no composite exists -- showing the transparent
              Trueview standalone was the bug this fixes. A raster/SVG artwork
              has no ``preview`` sidecar (dstPreview.kind === 'none') and goes
              straight through the composite path -- zero regression.

              Ordering (top wins):
                1. Artifi composite ready (previewUrl)  -> stage-image.
                2. Composite still loading              -> the Artifi shimmer.
                   NO stitch-render state may steal the surface from a
                   composite that is on its way -- including a READY one
                   (corrected 2026-09-02; rung 2 and rung 3 were the other
                   way round, which made a cold DST placement flash the bare
                   decoration and then swap to the garment).
                3. No composite (SETTLED), dst ready    -> stage-dst-image
                   (the honest fallback, so the Stage is never empty).
                4. No composite (settled), dst pending  -> stage-dst-pending.
                5. No composite (settled), dst failed   -> stage-dst-failed.

              SUPERSEDED FOR THE NO-COLORS CASE (Jake, 2026-09-28): when the
              product has NO colors selected (selected_color_skus is a loaded,
              empty array) the Stage shows stage-empty -- ahead of EVERY rung
              above, even when placements carry artwork. Rung 3's "never
              empty" rationale does not hold there: with no color there is
              no garment a shopper could buy, and the stitch render it fell
              through to (measured on 8086) floated on no garment at all.
              With at least one color selected, rungs 1-5 are unchanged.
            -->
            <!--
              NO COLORS SELECTED (Jake, 2026-09-28) -- the TOP of the chain.
              It outranks the unrouted-color branch too: routing is a
              per-color question, and with no colors there is no color to
              route. The composite (rungs 1-2) cannot fire here in practice
              -- cacheArgs needs a selected color and the enabled-color
              watcher clears the selection -- but the branch outranks it
              explicitly so a leaked url can never claim the surface.
              Stage.noColors.spec.js pins it.
            -->
            <div
                v-if="noColorsSelected"
                class="stage-empty"
                data-testid="stage-empty"
            >
                <p>{{ emptyStateMessage }}</p>
            </div>
            <!--
              UNROUTED GARMENT COLOR (fix, 2026-08-23) -- FIRST in the
              precedence chain, deliberately. It also outranks the
              approximate-placement branch immediately below (reconciled
              2026-08-31): with no decoration resolved for this color there is
              nothing to place approximately.

              `activeVariant` used to fall back to `variants[0]` when the
              kind-aware resolver missed, so the builder composited an
              ARBITRARY decoration onto a color that routes to none of
              them. The shopper path returns null on the same miss and
              renders nothing, so a distributor could preview a decoration
              on a color, launch, and have that color ship blank. This
              surface is the builder agreeing with the shopper.

              It sits ABOVE the Artifi branches because dropping the
              fallback does NOT stop the preview call: `cacheArgs` still
              fires with artworkFileId=null and the backend happily
              returns a BARE GARMENT render. That render would win
              `previewUrl` and the Stage would show a clean, undecorated
              shirt with no explanation -- which is exactly the silent lie
              this fix exists to remove. Claiming the surface first is
              what makes the gap legible.

              Copy is short and non-alarming on purpose: an unrouted color
              is a normal configuration gap a distributor hits while
              building, not an error. It names the color (so the user
              knows WHICH swatch), states what a shopper would see (the
              whole point), and points at the remedy.
            -->
            <div
                v-else-if="unresolvedColor"
                class="stage-unresolved-color"
                data-testid="stage-unresolved-color"
            >
                <p
                    class="stage-unresolved-color-title"
                    data-testid="stage-unresolved-color-title"
                >
                    No decoration is set up for {{ selectedColorLabel }}.
                </p>
                <p
                    class="stage-unresolved-color-body"
                    data-testid="stage-unresolved-color-body"
                >
                    A shopper choosing this color would see the garment
                    undecorated.
                </p>
                <p
                    class="stage-unresolved-color-remedy"
                    data-testid="stage-unresolved-color-remedy"
                >
                    Add this color to one of the artworks on this
                    placement, or add a catch-all that covers every color.
                </p>
            </div>
            <!--
              THE COLOR HAS AN UPLOADED IMAGE (Jake, 2026-10-28): the
              distributor's own image LEADS this color's images (the render follows it
              as the next image elsewhere; the Stage shows only the upload)
              wherever the color's image shows, the Stage included, also
              where Artifi can render it. It sits under the no-colors and
              unrouted-color states (those name a configuration gap first)
              and above every preview state; the fallback branches below
              show the same image inside their own tile, so they are not
              claimed here (showUploadedImage).
            -->
            <img
                v-else-if="showUploadedImage"
                :src="uploadedPreviewUrl"
                :alt="`Your preview image for ${selectedColorLabel}`"
                class="stage-image"
                data-testid="stage-custom-preview-image"
            />
            <!--
              ARTIFI CANNOT DRAW THIS STYLE (card #243, GH #4443): no color of
              it resolves to a renderable Artifi variant, or Artifi does not
              know the SKU. No preview request is made (liveCacheArgs), so
              nothing below could claim the surface with a real render; the
              Stage offers the fallbacks instead -- the distributor's own
              image for this color, a zone diagram, an open canvas. It sits
              under the no-colors and unrouted-color states (those name a
              configuration gap first) and ABOVE the #2874 approximation,
              whose job the zone diagram does here for every placement.
              Same tile seam as the render-error fallback below.
            -->
            <div
                v-else-if="showStyleFallback"
                class="stage-fallback stage-fallback--options"
                data-testid="stage-fallback"
                data-hint="style_unsupported"
            >
                <ArtifiFallbackPreview
                    reason="style_unsupported"
                    :message="styleUnsupportedMessage"
                    v-bind="fallbackProps"
                    @upload="onFallbackUpload"
                />
            </div>
            <!--
              APPROXIMATE PLACEMENT (GH #2874) -- second in the chain, and
              the position is load-bearing.

              It sits directly BELOW the unrouted-color branch above; the two
              landed independently and were reconciled 2026-08-31. An unrouted
              color means NO decoration resolves for the selected garment
              color, so there is nothing to approximate and that state must be
              named first. The reasoning below is otherwise unchanged: this
              branch still outranks `stage-image`.

              This branch handles a product carrying at least one placement on
              a zone Artifi has no camera for (sleeve / leg / hip on all but a
              handful of SKUs). It MUST win over `stage-image` below: on this
              exact case Artifi does return an image -- a garment with that
              decoration silently MISSING -- and rendering it as `stage-image`
              is the confidently-wrong preview this fixes. Anywhere later in
              the chain the composite would arrive first and the honest state
              would never be reached.

              It cannot steal any neighbour's case, because it requires a
              live placement on a zone that is invalid GIVEN AUTHORITY:
                * no placements            -> the sanmar swatch / silhouette
                  branches keep their case (showApproximatePreview is false).
                * skuViews unavailable     -> no authority, isZoneValidForSku
                  fails OPEN, this is false, and stage-image /
                  stage-shimmer / stage-fallback behave exactly as today.
                * every zone renderable    -> false; the normal Artifi path
                  runs untouched.
              Stage.approximatePlacement.spec.js asserts each of those
              neighbours still wins its own case, so a later edit that
              reorders the chain fails loudly.

              This EXTENDS the honest-degradation seam PR #2852 established
              (stage-fallback): same shape -- "we deliberately cannot show you
              the real composite, here is the honest reason" -- rather than a
              second parallel fallback path. It is a separate BRANCH only
              because it must outrank stage-image, which stage-fallback (a
              response to a backend error) does not and must not.
            -->
            <div
                v-else-if="showApproximatePreview"
                class="stage-approximate"
                data-testid="stage-approximate"
            >
                <div class="stage-approximate-canvas">
                    <GarmentSilhouette
                        :silhouette="approximateSilhouette"
                        :color="selectedColorForSilhouette"
                        view="front"
                        class="stage-approximate-silhouette"
                        data-testid="stage-approximate-silhouette"
                    />
                    <div
                        v-for="mark in approximateMarks"
                        :key="mark.key"
                        class="stage-approximate-mark"
                        data-testid="stage-approximate-mark"
                        :data-zone="mark.zone"
                        :data-exact="mark.exact ? 'true' : 'false'"
                        :style="mark.style"
                    >
                        <img
                            v-if="mark.artworkUrl"
                            :src="mark.artworkUrl"
                            :alt="`Approximate ${mark.zone} placement`"
                            class="stage-approximate-art"
                        />
                        <span
                            v-else
                            class="stage-approximate-art-fallback"
                            aria-hidden="true"
                            >{{ mark.initial }}</span
                        >
                    </div>
                </div>
                <p
                    class="stage-approximate-notice"
                    data-testid="stage-approximate-notice"
                >
                    {{ approximateNotice }}                </p>
            </div>
            <!--
              `!loading` (Jake, 2026-10-06): while a NEW render generates the
              cache wrapper still holds the PREVIOUS url, and showing that
              stale image until the new one swaps in read as "nothing is
              happening". The shimmer below claims the surface for the whole
              generation instead. The zoom lens is gated the same way.
            -->
            <!--
              A TICKED color whose render is deferred (Jake 2026-10-07): its
              blank or existing render, plus a short note. No Artifi call.
            -->
            <div
                v-else-if="deferredStandIn"
                class="stage-deferred"
                data-testid="stage-render-deferred"
            >
                <!--
                  The color's supplier photo (or its render once landed) stands
                  in while the render is pending; the shimmer is only the
                  placeholder when there is no photo to show.
                -->
                <img
                    v-if="deferredStandIn.url"
                    :src="deferredStandIn.url"
                    :alt="previewAlt"
                    class="stage-image stage-image--swatch"
                    data-testid="stage-render-deferred-image"
                />
                <RenderShimmer
                    v-else-if="deferredStandIn.pending"
                    class="stage-shimmer"
                    data-testid="stage-render-deferred-shimmer"
                    aria-hidden="true"
                />
                <p
                    v-if="deferredStandIn.state === 'pending'"
                    class="stage-deferred-note stage-deferred-rendering"
                    data-testid="stage-render-deferred-rendering"
                    role="status"
                >
                    <span
                        class="stage-deferred-spinner"
                        data-testid="stage-render-deferred-spinner"
                        aria-hidden="true"
                    />
                    Rendering...
                </p>
                <p
                    v-else-if="deferredStandIn.state === 'scheduled'"
                    class="stage-deferred-note"
                    data-testid="stage-render-deferred-note"
                >
                    Preview renders shortly.
                </p>
                <button
                    v-else-if="deferredStandIn.state === 'idle'"
                    type="button"
                    class="stage-render-preview"
                    data-testid="stage-render-preview"
                    @click="onRenderPreview"
                >
                    Render preview
                </button>
            </div>
            <img
                v-else-if="previewUrl && !loading"
                ref="stageImageEl"
                :src="previewUrl"
                :alt="previewAlt"
                class="stage-image"
                data-testid="stage-image"
                :data-colorway-state="colorwayStateValue || null"
                @load="onImageLoad"
                @mousemove="onMouseMove"
                @mouseleave="onMouseLeave"
            />
            <!--
              The Artifi shimmer outranks EVERY stitch-render state,
              including a ready one (Jake, 2026-09-02).

              This branch used to sit BELOW `dst ready`. On a cold composite
              (loading=true, previewUrl still null) the flat transparent
              Trueview raster therefore claimed the surface at full bleed and
              was swapped for the on-garment composite a moment later. Jake,
              driving a DST placement on 8089: "the stage fills with the
              decoration instead of the on-garment preview rendering. Once
              the render is done it shows the garment, but the transition is
              jarring."

              Two DIFFERENT images back to back is what reads as jarring --
              not the wait. The rule was already established one branch down
              for a pending render ("a pending/failed stitch render must NOT
              steal the surface from a composite that is on its way"); it was
              simply never extended to a READY one, and readiness is exactly
              what makes the theft visible rather than harmless.

              The stitch render stays the honest fallback once the composite
              has SETTLED without producing a url -- that is the branch below,
              and it is unchanged. What moved is only the still-loading case.
            -->
            <RenderShimmer
                v-else-if="loading"
                class="stage-shimmer"
                data-testid="stage-shimmer"
                role="status"
                aria-label="Loading preview"
            />
            <!--
              The bare stitch render has NO garment behind it (transparent
              Trueview), so it goes through DecorationArtThumb, the one
              mechanism for off-garment decoration art (#4075): a light or
              dark backdrop picked from the artwork's server-sampled ink, and
              the component's own fallback when the ink is unknown. Its
              data-backdrop / data-backdrop-source attributes are what tests
              read.
            -->
            <DecorationArtThumb
                v-else-if="dstPreview.kind === 'ready'"
                :src="dstPreview.previewUrl"
                :ink-hex="dstArtwork?.dominant_ink_hex ?? null"
                :ink-mixed="dstArtwork?.dominant_ink_mixed === true"
                :alt="previewAlt"
                class="stage-image"
                data-testid="stage-dst-image"
            />
            <div
                v-else-if="dstPreview.kind === 'pending'"
                class="stage-dst-pending"
                data-testid="stage-dst-pending"
                role="status"
                aria-label="Generating preview"
            >
                <RenderShimmer class="stage-shimmer" aria-hidden="true" />
                <p class="stage-dst-text">Generating preview...</p>
            </div>
            <div
                v-else-if="dstPreview.kind === 'failed'"
                class="stage-dst-failed"
                data-testid="stage-dst-failed"
            >
                <p class="stage-dst-text">{{ dstPreview.message }}</p>
            </div>
            <!--
              Graceful structured-404 fallback (Bug 3 -- 2026-06-18).
              The backend returns a 404 with hint=sku_not_configured when
              Artifi's GetImage 500s on an unconfigured SKU, and a 404
              with hint=artwork_not_registered when the resolved Artwork
              has no artifi_unique_name yet. It also returns render_failed
              (Artifi GetImage 500) and budget_exceeded (monthly render
              budget reached -- temporary and system-wide). In every case
              we render a clear placeholder tile rather than letting the
              editor fall through to a generic error banner (or, for
              budget_exceeded before 2026-08-12, to nothing at all). The optional
              placeholder_url is the Artwork's thumbnail/full image -- a
              best-effort "this is what the artwork looks like" even when
              the composite isn't available.
            -->
            <div
                v-else-if="previewFallback"
                class="stage-fallback"
                :class="{ 'stage-fallback--options': showHintFallback }"
                data-testid="stage-fallback"
                :data-hint="previewFallback.hint"
            >
                <img
                    v-if="previewFallback.placeholder_url && !showHintFallback"
                    :src="previewFallback.placeholder_url"
                    :alt="previewAlt"
                    class="stage-fallback-img"
                    data-testid="stage-fallback-img"
                />
                <p
                    class="stage-fallback-text"
                    data-testid="stage-fallback-text"
                >
                    {{ previewFallback.message }}
                </p>
                <!--
                  The "Request preview support for this color" button was
                  removed 2026-07-22 (Jake): it was a stub that only logged to
                  the console and filed nothing. The real feature -- persist a
                  preview-support request + a superadmin review UI -- is tracked
                  as a post-MVP Boulder card (https://trello.com/c/zc7a4TCD).
                  Re-add the affordance here, wired to the real endpoint, when
                  that card ships. The product is already orderable; the
                  message above is the honest state.
                -->
                <!--
                  Unrenderable-colors blueprint 5.4 (Phase 6): the Stage
                  states the removal explicitly, reusing the SAME
                  coverageFootnote text composition as ColorsPanel/
                  ColorFilmstrip (colorRemovedNotice below), instead of
                  relying only on the generic backend-driven fallback
                  message above. Renders only when coverageFootnote has
                  data for the CURRENTLY-SELECTED color -- keeps the three
                  surfaces from ever disagreeing about a color's state.
                -->
                <p
                    v-if="colorRemovedNotice"
                    class="stage-canvas-removed-notice"
                    data-testid="stage-canvas-removed-notice"
                >
                    {{ colorRemovedNotice }}
                </p>
                <!--
                  Card #243: a render Artifi could not produce for this color
                  (sku_not_configured / render_failed) gets the same fallbacks
                  as an unsupported style, under the tile's own message. Not
                  for budget_exceeded (temporary, system-wide) or
                  artwork_not_registered (an artwork problem a mockup image
                  would paper over).
                -->
                <ArtifiFallbackPreview
                    v-if="showHintFallback"
                    :reason="previewFallback.hint"
                    v-bind="fallbackProps"
                    @upload="onFallbackUpload"
                />
            </div>
            <!--
              No-placement SanMar preview (LAST-RESORT, after the Artifi
              bare-render paths above). For a product with no decoration
              placements, the blank-garment auto-pick drives an Artifi bare
              render via previewUrl; these SanMar branches only fire when
              Artifi produced nothing (no loading, no url, no structured
              fallback). Ordering matters: placing these before the Artifi
              paths would swallow the loading/preview/fallback states for a
              no-placement product (a pre-existing bug the old early-position
              stage-empty had; see Stage.preview.spec + Stage.spec).

              Show the SanMar product photo for the selected color from
              supplierMeta.image_urls_by_color (correctly date-folder-resolved,
              getMediaContent-first with per-color EPDD FLAT fallback). Never
              the swatch chip -- a genuinely-photoless color falls to the
              tinted GarmentSilhouette below.

              The same blank also stands in for a render that FAILED without
              an explanatory tile (showColorBlank, 2026-10-08).
            -->
            <img
                v-else-if="showColorBlank && sanmarPreviewUrl"
                :src="sanmarPreviewUrl"
                :alt="previewAlt"
                class="stage-image stage-image--swatch"
                data-testid="stage-sanmar-swatch"
            />
            <!--
              No-placement, genuinely-photoless color: render a tinted garment
              silhouette instead of the swatch chip (2026-08 fix). A color
              reaches here only when it has no real product photo in
              image_urls_by_color (getMediaContent empty AND no EPDD flat image
              -- see api/src/endpoints/sanmar_feed.py) and Artifi rendered
              nothing. Tinted to the color's hex, shaped by the EPDD
              category/subcategory. No photoUrl -> GarmentSilhouette renders
              the tint, not a photo.
            -->
            <GarmentSilhouette
                v-else-if="showColorBlank && editorUi.selectedColorSku && !sanmarPreviewUrl"
                :silhouette="silhouetteClass"
                :color="selectedColorForSilhouette"
                view="front"
                class="stage-image"
                data-testid="stage-sanmar-silhouette"
            />
            <div
                v-else-if="!hasAnyArtwork && !showDashedOverlay"
                class="stage-empty"
                data-testid="stage-empty"
            >
                <p>{{ emptyStateMessage }}</p>
            </div>
            <!--
              The server's word on the bound colorway for the image above
              (P0, Jake 2026-10-28). `applied` -> a screen-reader status
              only; `pending` / `unavailable` -> a short honest note, shown
              under the still-visible original-colors image.
            -->
            <p
                v-if="previewUrl && !loading && colorwayNote"
                class="stage-colorway-note"
                role="status"
                data-testid="stage-colorway-note"
                :data-colorway-state="colorwayStateValue"
            >
                {{ colorwayNote }}
            </p>
            <span
                v-else-if="previewUrl && !loading && colorwayStateValue === 'applied'"
                class="stage-colorway-applied"
                role="status"
                data-testid="stage-colorway-applied"
                >Preview shows the selected colors for this decoration.</span
            >
            <!--
              Catalog-driven dashed zone overlay (Phase F F3.c).

              The SVG viewBox is sized to the product photo's pixel
              dimensions returned by the catalog (e.g. 1200x1800 for
              SanMar 18000). ``preserveAspectRatio="xMidYMid meet"``
              letterboxes the SVG the same way the ``<img>`` letterboxes
              its source (``object-fit: contain``) -- so the overlay
              rect lines up with the actual rendered image rectangle,
              even when the container's aspect ratio doesn't match the
              photo's.

              Rect coords are pixel-space ``x_px / y_px / w_px / h_px``
              from Artifi's template metadata, sized to ``image_*_px``.
              See ``frontend/src/utils/resolvePlacementBox.js`` for the
              (zone, view) -> box selection logic.
            -->
            <div
                v-if="showDashedOverlay && overlayBox && !noColorsSelected"
                data-testid="stage-zone-overlay"
                class="stage-zone-overlay"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    :viewBox="`0 0 ${overlayBox.image_width_px} ${overlayBox.image_height_px}`"
                    preserveAspectRatio="xMidYMid meet"
                    class="stage-zone-overlay-svg"
                    :data-testid-source="overlayBox.source"
                >
                    <rect
                        :x="overlayBox.x_px"
                        :y="overlayBox.y_px"
                        :width="overlayBox.w_px"
                        :height="overlayBox.h_px"
                        fill="none"
                        stroke="currentColor"
                        :stroke-width="overlayStrokeWidth"
                        :stroke-dasharray="overlayStrokeDasharray"
                        data-testid="stage-zone-overlay-rect"
                    />
                </svg>
            </div>
            <!--
              Amazon-style in-place hover-zoom lens (loupe). A floating
              magnified region that follows the cursor over the preview
              image. Its CSS background is the SAME previewUrl image scaled
              up + offset (background-position) so it shows the region under
              the cursor -- so it magnifies the IMAGE ONLY and never the
              dashed-zone SVG overlay above (those are placement guides, not
              "what it looks like"). Pure mouse affordance:
              ``pointer-events: none`` so it never eats the underlying
              image's mouse events, and it's only present when a real,
              loaded preview image is on screen. Geometry lives in
              useHoverZoom.js (letterbox-aware; zoom factor capped for
              low-res renders).

              The gate is `previewUrl && !loading && !unresolvedColor`, matching the
              <img> above -- NOT `previewUrl` alone. On an unrouted color
              the bare-garment render still populates previewUrl, but the
              image it belongs to is not mounted (the unresolved-color
              surface claims the canvas), so a lens gated on previewUrl
              alone would float over the empty state magnifying an image
              that is not there. The img+lens pair must stay gated
              together. `!noColorsSelected` follows the same rule for the
              no-colors empty state (2026-09-28).
            -->
            <div
                v-if="previewUrl && !loading && !unresolvedColor && !noColorsSelected && !showUploadedImage"
                class="stage-zoom-lens"
                data-testid="stage-zoom-lens"
                aria-hidden="true"
                :style="lensStyle"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import RenderShimmer from "@/components/decoration/RenderShimmer.vue";
import { useStore } from "vuex";
import { useProductEditorStore } from "@/stores/productEditor";
import { useProductEditorUiState } from "@/stores/productEditorUiState";
import { useImpersonationStore } from "@/stores/impersonation";
import { useArtifiPreviewCache } from "@/composables/useArtifiPreviewCache";
import { COLORWAY_STATE, previewColorwayArgs } from "@/utils/previewColorway";
import {
    useArtifiPreviewCacheStore,
    buildCacheKey,
} from "@/stores/artifiPreviewCache";
import { useStorefrontColorRendersStore } from "@/stores/storefrontColorRenders";
import { useArtifiCoverage } from "@/composables/useArtifiCoverage";
import { useArtifiProductViews } from "@/composables/useArtifiProductViews";
import { artifiPreviewEligible } from "@/utils/supplier-entitlement";
import { pickPreviewImage } from "@/utils/supplierPreviewImage";
import { silhouetteFor } from "@/utils/silhouetteFor";
import GarmentSilhouette from "@/components/Storefront/GarmentSilhouette.vue";
import DecorationArtThumb from "@/components/decoration/DecorationArtThumb.vue";
import ArtifiFallbackPreview from "./ArtifiFallbackPreview.vue";
import ColorImageStrip from "./ColorImageStrip.vue";
import { usePreviewFallbackTools } from "@/composables/usePreviewFallbackTools";
import { colorUploadUrl, includeColorRender } from "@/utils/storefrontImages";
import { useArtworksStore } from "@/stores/artworks";
import { useVariantResolver } from "@/composables/useVariantResolver";
import { usePlacementZoneBoxes } from "@/composables/usePlacementZoneBoxes";
import { resolvePlacementBox } from "@/utils/resolvePlacementBox";
import { previewDisplay } from "@/utils/artworkPreviewState";
import { isEmbroiderySource } from "@/utils/artworkUploadContract";
import { useHoverZoom } from "./useHoverZoom";
import {
    strictViewCodeForSku,
    resolveViewCodeForSku,
    frontGroupCodesForSku,
    backGroupCodesForSku,
    isZoneValidForSku,
} from "@/utils/placementZoneToViewCode";
import {
    hotspotFor,
    photoBoxFor,
    viewBoxFor,
    zoneInchesFor,
    overlayUnitsFor,
} from "@/components/decoration/placementPreviewGeometry";

const editor = useProductEditorStore();
const editorUi = useProductEditorUiState();
const vuexStore = useStore();
const impersonation = useImpersonationStore();
const { resolve: resolveVariantForColor } = useVariantResolver();

// Day 8 Item A1: a SuperAdmin without an active impersonation session
// has no effective_distributor_id() server-side. Every Artifi preview
// call returns 403. Short-circuit cacheArgs to null so the composable
// does not fire and the SaImpersonationBanner explains the cause to the
// SA above the Stage.
const isSuperAdminWithoutImpersonation = computed(
    () =>
        Boolean(vuexStore?.getters?.isSuperAdmin) &&
        !impersonation.isActive,
);

// Cross-decoration {variant_id -> artwork_file_id|null} map. Used for
// the cacheArgs fingerprint so the Artifi preview cache invalidates
// whenever any variant's artwork binding changes. PR-N4: keyed by
// variant row id instead of the dropped variant_key column.
// What each variant puts on the garment, as the preview cache's
// invalidation input. Two axes, because two things change the picture:
//
//   * artworkFileId -- WHICH file is composited.
//   * colorwayId    -- which THREADS that file is rendered in. Rebinding a
//     colorway leaves the .dst untouched but makes Artifi return a
//     different composite, so it has to be here or the LRU serves the
//     previous colorway's image indefinitely (card
//     https://trello.com/c/vc8U5340 -- Jake: "if I change it then the
//     preview needs to update accordingly").
//
// Deliberately reads `colorway_id` (the FK / provenance pointer) and not
// `colorway_threads_json`. The snapshot is an array whose identity churns on
// every reload even when the threads are unchanged, which would evict the
// cache on load and buy a paid re-render for nothing. The FK changes exactly
// when the user rebinds, which is exactly when the picture changes.
const variantBindings = computed(() => {
    const out = {};
    const decos = editor.decorations ?? [];
    for (const deco of decos) {
        const variants = deco.variants ?? [];
        for (const v of variants) {
            const k = v?.id ?? v?._tempId ?? null;
            if (k != null) {
                out[String(k)] = {
                    artworkFileId: v.artwork_file_id ?? null,
                    colorwayId: v.colorway_id ?? null,
                };
            }
        }
    }
    return out;
});

// product_item_number from the campaign_product, when loaded.
const productItemNumber = computed(
    () => editor.serverState?.data?.attributes?.product_item_number ?? null,
);

// Supplier gate (2026-07-10, Jake -- P0 regression fix). The Colors tab
// mounts this Stage, whose two Artifi callers below (supported_color_variants
// via useArtifiCoverage, product_views via useArtifiProductViews) previously
// fired for EVERY product -- so a NAMED non-SanMar product 502'd both
// endpoints. Only SanMar-backed products have Artifi coverage; a named
// non-SanMar product has no Artifi preview to render anyway. Feed the callers
// a null SKU for a named non-SanMar supplier -- both composables' watchers
// skip the fetch on a null/non-string SKU, so neither request fires. The gate
// keys on the effective supplier_name via isSanmarSupplierName (SanMar-named
// OR unknown/unset supplier is eligible). REPLACES the old is_smart_product
// gate, which wrongly skipped real SanMar products whose flag is false/null
// (the P0 this repairs). Mirrors ColorFilmstrip.vue / ColorsPanel.vue /
// StorefrontListingPanel.vue. See @/utils/isSanmarSupplier.
// Keyed on the resolved supplier_key since 2026-10-07 (S&S previews): see
// artifiPreviewEligible in @/utils/supplier-entitlement.
const isArtifiEligible = computed(() =>
    artifiPreviewEligible({
        supplierKey: editor.supplierKey,
        supplierKeyResolved: editor.supplierKeyResolved,
        supplierName: editor.getEffectiveAttribute?.("supplier_name"),
    }),
);
const artifiSku = computed(() =>
    isArtifiEligible.value ? productItemNumber.value : null,
);

// Bug #1884: fetch the CURRENT SKU's Artifi-configured views and feed
// them into productEditorUiState. The store's selectedView computed then
// resolves against the SKU's real views instead of the hardcoded SKU-
// 18000 static map -- so a polo/jacket (K540/JST70/ST650) that has no
// "Full Center" view never sends that code to Artifi (which 500s on it
// and the backend then mislabels as sku_not_configured). Falls back to
// the legacy static behavior when the fetch hasn't landed / errored
// (setSkuViews(null)), so 18000 / PC54 keep working.
const { views: skuProductViews } = useArtifiProductViews(artifiSku);
watch(
    skuProductViews,
    (views) => {
        if (typeof editorUi.setSkuViews === "function") {
            editorUi.setSkuViews(views);
        }
    },
    { immediate: true },
);

// 2026-06-19 -- Option C variant SKU resolution. The Stage consumes the
// Artifi-supported color set + its name->variant_sku map so the preview
// call can include the resolved Artifi-numeric variant SKU when one is
// already known on the client. The backend resolves anyway (covers CSV
// reverse-lookup cases the frontend can't easily replicate), but passing
// the variant SKU up-front saves the backend a catalog lookup on cache
// miss and keeps the URL contract honest (variant_sku param matches the
// Artifi-side schema for already-mapped colors).
//
// Unrenderable-colors blueprint 5.4 (Phase 6): the SAME useArtifiCoverage
// call also exposes coverageFootnote -- the Stage does NOT need a second
// composable instance (the module-level cache/inflight maps make a second
// call for the same SKU free anyway; this is simply reusing the one call
// already made here).
//
// Card #243 / GH #4443: the same call answers styleUnsupported -- Artifi can
// render NO color of this style. The Stage then makes no preview request
// (liveCacheArgs) and shows the fallbacks (showStyleFallback).
const {
    supportedWithVariantSku: artifiVariantSkuMap,
    coverageFootnote,
    styleUnsupported: artifiStyleUnsupported,
    loading: artifiCoverageLoading,
} = useArtifiCoverage(artifiSku);

// A FUNCTION DECLARATION for the same TDZ reason as isApproximatePreview
// below: liveCacheArgs consults it.
function isArtifiStyleUnsupported() {
    return Boolean(artifiStyleUnsupported?.value);
}

// Coverage has not answered yet. Until it does we cannot know the style is
// renderable, and a preview request sent meanwhile is spent on a style that
// may be unsupported (measured on 8089: request at 745ms, coverage at
// 817ms). Not loading for a non-Artifi product (its SKU is null).
function isArtifiCoveragePending() {
    return Boolean(artifiCoverageLoading?.value);
}

// Lookup helper: try exact match first, then a casefold + space-strip
// fallback for SanMar slash colors ("Black/ Red") vs Artifi's no-space
// catalog form ("Black/Red"). Returns null if no client-side match --
// backend resolution then does the CSV reverse-lookup.
function _normalizeColor(name) {
    if (typeof name !== "string") return "";
    let out = "";
    for (const ch of name.toLowerCase()) {
        // Keep alphanumerics. Drop spaces / punctuation. Mirrors the
        // backend's resolver._normalize_color_name so the two layers
        // agree on equivalence classes.
        if (/[a-z0-9]/.test(ch)) {
            out += ch;
        }
    }
    return out;
}

function _resolveArtifiVariantSkuClientSide(colorName) {
    if (!colorName) return null;
    const map = artifiVariantSkuMap.value || {};
    if (Object.prototype.hasOwnProperty.call(map, colorName)) {
        return map[colorName];
    }
    const queryNorm = _normalizeColor(colorName);
    if (!queryNorm) return null;
    for (const [k, v] of Object.entries(map)) {
        if (_normalizeColor(k) === queryNorm) return v;
    }
    return null;
}

// Tier 1 activePreview (design-philosophy principle 4): consume
// productEditorUiState.effectivePlacement -- the ONE placement the Stage
// is looking at (the user's selection, else the FIRST placement, else
// null for a bare product). The rendered view (editorUi.selectedView)
// derives from the SAME value inside the store, so the view and the
// artwork below can never describe two different placements. The old
// no-placement fallback here scanned ALL decorations for the first
// artwork while selectedView independently defaulted to the front --
// which composited a back placement's logo onto the front view on a
// fresh load (the reported bug).
const activePlacement = computed(() => editorUi.effectivePlacement);

// PR-N2: resolve the active variant via the kind-aware resolver. When
// a placement + color are active, the resolver consults specific_colors
// > catch_all (L/D) > single_catch_all per the backend's resolution
// order; the legacy A/B "variantRoutingMap" override channel collapses
// into manual_ld (already consulted inside the resolver). With no
// active placement (product has none), there is nothing to composite --
// the Stage renders the bare garment.
//
// A RESOLVER MISS RESOLVES TO NOTHING -- the builder must agree with the
// shopper (fix, 2026-08-23). This computed used to end in
// `return variants[0]`, compositing an ARBITRARY variant's artwork onto a
// color that routes to none of them. The shopper path has no such
// fallback: the backend resolver returns None on a miss
// (api/src/services/variant_resolver.py :104, :192, :196, :250,
// :289-293) and the frontend mirror returns null
// (useVariantResolver.js:169); a grep over components/Storefront and
// pages/portal finds zero variants[0] fallbacks. So a distributor could
// preview a decoration on a garment color, launch the store, and that
// color would ship BLANK -- and nothing raised. The `unresolvedColor`
// state below names that gap on the canvas instead of letting the Stage
// quietly lie.
//
// The `variants[0]` anchor survives ONLY for the no-color-selected case.
// That is not a miss -- there is no color to route yet, so there is
// nothing to disagree with the shopper about; the anchor just gives the
// bare-garment render a variant to hang its Artifi args on.
const activeVariant = computed(() => {
    if (!activePlacement.value) return null;
    const variants = activePlacement.value.variants ?? [];
    if (!variants.length) return null;
    if (editorUi.selectedColorSku) {
        const placementId =
            activePlacement.value.id ?? activePlacement.value._tempId;
        return resolveVariantForColor(
            placementId,
            editorUi.selectedColorSku,
        );
    }
    return variants[0];
});

// True when a garment color IS selected, the active placement HAS
// variants, and NONE of them routes that color -- every resolver tier
// missed (color_overrides, then specific_colors, then catch_all_light /
// catch_all_dark, then single_catch_all).
//
// This is a ROUTING gap, and it is deliberately narrower than "the Stage
// has nothing to show":
//   - A placement with NO variants is "awaiting artwork", not unrouted --
//     the dashed zone overlay already says that, and stealing that path
//     would put a routing-gap message on the ordinary "I just added a
//     placement" flow.
//   - Low contrast is NOT this state (a low-contrast color resolves
//     fine), and neither is "no stitch file yet" (that is the production
//     axis, surfaced by dstPreview).
//
// It happens when a distributor routes SOME garment colors and adds no
// catch-all, and a shopper then picks an unrouted color.
const unresolvedColor = computed(() => {
    if (!editorUi.selectedColorSku) return false;
    const p = activePlacement.value;
    if (!p) return false;
    if (!(p.variants ?? []).length) return false;
    return !activeVariant.value;
});

// Human-readable name for the selected colorway, for the empty state's
// copy. Falls back to the raw SKU on a supplierMeta cache miss -- the
// rest of the editor tolerates that miss, and rendering "undefined" at
// the user would be worse than rendering the SKU.
const selectedColorLabel = computed(() => {
    const sku = editorUi.selectedColorSku;
    if (!sku) return "";
    const row = (editor.supplierMeta?.colors ?? []).find(
        (c) => c.color_sku === sku,
    );
    return row?.name || sku;
});

// DST/EMB stitch-file render state (PR-6, plan 5.3b; precedence corrected
// 2026-08-03). A stitch file's raw render is the artwork_preview sidecar
// {state, preview_url} (PR-4) carried on the active variant -- the flat
// Wilcom Trueview raster on a transparent background, NOT the on-garment
// Artifi composite. The shared previewDisplay helper maps it to a display
// kind so the Stage and the library grid map the states identically. In the
// Stage this raw render is only a FALLBACK: the Artifi composite (previewUrl)
// wins whenever it is available; the raw render fills in when there is no
// composite (see the template precedence chain). A raster/SVG artwork (or no
// active variant) has no preview -> kind 'none' -> the Stage renders its
// existing Artifi composite path (zero regression).
const dstPreview = computed(() =>
    previewDisplay(activeVariant.value?.preview),
);

// The active variant's artwork, for the ink DecorationArtThumb picks a
// backdrop from: dominant_ink_hex (sampled server-side from this same
// Trueview) and dominant_ink_mixed (two-tone art, #4086: neutral). Read
// from the artwork repository the editor hydrates on open -- the same source
// VariantCard's thumbnail uses. Null when unknown; the component then falls
// back on its own.
const artworks = useArtworksStore();
const dstArtwork = computed(
    () => artworks.getArtworkForVariant?.(activeVariant.value) ?? null,
);

// Variant SKU sent to Artifi as the `sku` param. Per the Artifi client
// docstring at api/src/integrations/artifi/client.py:204, this MUST be
// the color-specific variant SKU (e.g., "K500REDXL"), NOT the master /
// productItemNumber (e.g., "K500"). The filmstrip emits color_sku as
// the canonical color-specific SKU into editorUi.selectedColorSku
// (ColorFilmstrip.vue:99).
//
// Falls back to productItemNumber / variant.sku only when no color is
// selected -- that branch is effectively unreachable in steady state
// because cacheArgs already gates on selectedColorSku being non-empty
// (see the gate below). Belt-and-suspenders.
//
// Historical: the prior implementation preferred productItemNumber over
// selectedColorSku, which caused every color swatch click to render the
// default colorway. Jake observed this on /distributor/product-editor/71
// (dark red/maroon every click).
const variantSku = computed(() => {
    if (editorUi.selectedColorSku) return editorUi.selectedColorSku;
    if (productItemNumber.value) return productItemNumber.value;
    const v = activeVariant.value;
    return v?.sku ?? null;
});

// P2-2(a) close-out (PR #1913 gate review): view-compatibility gate on
// the COMPOSITED artwork. When the user flips the camera (StageHeader
// view strip / Front-Back override) to a view whose group does NOT
// contain the effective placement's own view, compositing that
// placement's artwork onto the flipped camera renders the artwork on
// the wrong side of the garment (back logo on a front camera). The
// dashed overlay already suppresses itself via resolvePlacementBox's
// group gate; this computed makes the composite request agree with it
// (design-philosophy principle 4) by sending artworkFileId=null -- the
// bare garment on the chosen camera is the honest render (principle 5).
//
// Resolution mirrors the overlay path: the placement zone maps to its
// real view via strictViewCodeForSku (live skuViews authoritative,
// static map fallback); compatibility is group membership (a Left
// Chest decoration IS visible on the Full Center front camera, so
// same-group composites stay legitimate). A placement with no zone
// info fails OPEN (no authority to declare a mismatch -- preserves
// compositing on legacy rows without zone data). A zone with NO valid
// view on this SKU (strict resolver returns null) fails CLOSED,
// matching the hidden overlay.
const artworkCompatibleWithView = computed(() => {
    const p = activePlacement.value;
    if (!p) return true; // no placement -> nothing to composite anyway
    const zone =
        p.placement_zone_slug ||
        p.placement ||
        p.zone ||
        p.zone_code ||
        null;
    if (!zone) return true; // no zone info -> fail open
    const placementView = strictViewCodeForSku(zone, editorUi.skuViews);
    if (!placementView) return false; // zone invalid for SKU -> bare render
    const view = editorUi.selectedView;
    if (!view || view === placementView) return true;
    const frontSet = frontGroupCodesForSku(editorUi.skuViews);
    const backSet = backGroupCodesForSku(editorUi.skuViews);
    if (frontSet.has(placementView)) return frontSet.has(view);
    if (backSet.has(placementView)) return backSet.has(view);
    // Placement view in neither group (unclassifiable) -> fail open.
    return true;
});

// Multi-placement composite preview (card https://trello.com/c/pI48s0zf).
// When the active view's GROUP has MORE THAN ONE placement, the Stage
// requests a COMPOSITE render (all those placements on one image) instead of
// the single-artwork render of just the effective placement. v2: the backend
// composites ThreadFlow-side (render each placement on its own view, PIL-merge
// onto the shared bare garment) because a single GetImage call cannot position
// two overlays in two different view canvases. The frontend contract is
// unchanged from #2019: send campaign_product_id when the active view's group
// has >1 placement. The composite stays DERIVED from the SAME
// effectivePlacement model (design-philosophy principle 4): effectivePlacement
// still decides the active VIEW; the composite simply shows every placement on
// that view's group. Single-placement + bare-garment cases keep the single
// path.

// The campaign_product_id for the composite request -- the JSON:API resource
// id of the loaded campaign_product.
const campaignProductId = computed(() => {
    const raw = editor.serverState?.data?.id;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
});

// Classify a view code into its front/back group from the SKU's LIVE view
// payload (Phase 3, #2224 -- the SKU-18000 static group sets are gone). The
// live group sets are data-driven: each derives from ``skuViews[].group``
// ("Front"/"Rear"), falling back to a view-code-name heuristic when Artifi's
// ``group`` is absent (K540 returns group=None). A selected view like "Left
// Chest" classifies into the front group via those live sets, so the
// composite still fires on a specific-view selection. A view with no
// resolvable group (or no live views) returns null (no composite) -- the
// fail-open "no authority" behavior.
function groupForView(view) {
    if (!view) return null;
    const liveFront = frontGroupCodesForSku(editorUi.skuViews);
    const liveBack = backGroupCodesForSku(editorUi.skuViews);
    if (liveFront.has(view)) return { group: liveFront, side: "front" };
    if (liveBack.has(view)) return { group: liveBack, side: "back" };
    return null;
}

// Count placements whose resolved view shares the active view's group. The
// composite shows only placements sharing the active view's group (a
// rear-group logo never composites onto a front camera). When classification
// yields a static-fallback group but the live skuViews would resolve a zone
// into a DIFFERENT-named code, we classify each placement's resolved code with
// the SAME groupForView fallback so both sides agree.
const activeGroupPlacementCount = computed(() => {
    const active = groupForView(editorUi.selectedView);
    if (!active) return 0;

    const decos = editor.decorations ?? [];
    let count = 0;
    for (const d of decos) {
        const zone =
            d.placement_zone_slug ||
            d.zone ||
            d.zone_code ||
            d.placement ||
            null;
        const zoneView = resolveViewCodeForSku({
            zoneCode: zone,
            skuViews: editorUi.skuViews,
        });
        const zoneGroup = groupForView(zoneView);
        if (zoneGroup && zoneGroup.side === active.side) count += 1;
    }
    return count;
});

// Fire the composite path only when there is a real multi-placement group AND
// we have the campaign_product_id to request it with.
const useComposite = computed(
    () =>
        activeGroupPlacementCount.value > 1 && campaignProductId.value != null,
);

const liveCacheArgsIgnoringCoverage = computed(() => {
    // Day 3 (audit gap #4): build cacheArgs even when no
    // variant has bound artwork. The composable now supports a bare-
    // render path (artworkFileId=null) so the backend returns a
    // garment-only preview. The dashed-zone overlay then floats OVER
    // the rendered garment instead of over white space.
    //
    // We still require variantSku — the backend needs to know which
    // product + view to render. When there is genuinely no anchor
    // variant (the product has NO placements, so activePlacement and
    // therefore activeVariant are null), artworkFileId goes out null
    // and the backend returns the bare-garment render.
    // Day 8 Item A1: SA without impersonation -> backend 403s. Don't fire.
    if (isSuperAdminWithoutImpersonation.value) return null;
    if (!variantSku.value) return null;
    // Card #243: Artifi can render no color of this style, so every call
    // would fail -- and Artifi is metered. The fallbacks render instead.
    if (isArtifiStyleUnsupported()) return null;
    // (The coverage hold lives in liveCacheArgs below, so the Stage can tell
    // "waiting on coverage" from "nothing to render".)
    // No-placement guard: when the product has no decoration placements,
    // there is no artwork to composite and the bare-garment Artifi call
    // returns an API error (the backend needs a configured SKU context).
    // Instead show the SanMar supplier swatch image (see noPlacements
    // computed below and the stage-sanmar-preview branch in the template).
    if ((editor.decorations ?? []).length === 0) return null;
    // Day 3 gate round-1 P1: also require a non-empty selectedColorSku.
    // Without a color, the bare-render call goes out with color_sku=null
    // and Artifi either 500s or returns a default-color render that
    // doesn't match the user's actual selection — neither is desirable.
    // On mount, productEditorUiState.selectedColorSku is null until the user
    // (or a Day 4 init step) picks a color; until then, don't fire.
    if (!editorUi.selectedColorSku) return null;
    // #2874: at least one placement is on a zone this SKU has no Artifi
    // camera for. Artifi cannot place it, so a render here returns a garment
    // with that decoration silently MISSING -- and the Stage would present it
    // as the truth. Jake's decision is to approximate it ourselves "without
    // calling Artifi at all": Artifi is metered, paid and rate-limited, so
    // burning a call to produce a misleading image is strictly worse than not
    // calling. The approximate branch renders instead (see
    // showApproximatePreview). Inert without authority -- isZoneValidForSku
    // fails OPEN on unavailable skuViews, so a slow fetch never suppresses a
    // legitimate render.
    if (isApproximatePreview()) return null;
    const v = activeVariant.value;
    // 2026-06-19 Option C: prefer the Artifi-numeric variant SKU when the
    // coverage map already knows it. Falls back to the SanMar color label
    // for backend-side resolution (master_sku + color_name params).
    const clientResolvedVariantSku = _resolveArtifiVariantSkuClientSide(
        editorUi.selectedColorSku,
    );
    const previewArtworkFileId = useComposite.value
        ? null
        : artworkCompatibleWithView.value
          ? (v?.artwork_file_id ?? null)
          : null;
    return {
        productSku: productItemNumber.value,
        colorSku: editorUi.selectedColorSku,
        viewCode: editorUi.selectedView,
        // Multi-placement composite (card https://trello.com/c/pI48s0zf):
        // when the active view's group has >1 placement, request the composite
        // (backend composites all of them from the placement model, ThreadFlow-
        // side) and send NO single-artwork file id. campaignProductId being
        // non-null both routes the composable to the composite path and folds
        // into the cache key so composites cache distinctly.
        campaignProductId: useComposite.value ? campaignProductId.value : null,
        // P2-2(a): only composite the placement's artwork when the
        // current camera can honestly show it (see
        // artworkCompatibleWithView above). On a flipped camera outside
        // the placement's view group, send null -> bare garment render.
        // In the composite path artworkFileId is unused (omitted below).
        artworkFileId: previewArtworkFileId,
        // The placement being previewed. The SERVER derives the bound
        // colorway from it (#4721): without it every preview is the design's
        // default colors, whatever the variant card says (P0, 2026-10-28).
        // colorwayId is request identity only -- never sent -- so a rebind
        // can't be answered from the previous colorway's cache entry. Named
        // for the single-artwork path only; the composite derives its
        // colorways from the campaign product's placements server-side.
        ...previewColorwayArgs({
            placement: activePlacement.value,
            variant: v,
            artworkFileId: previewArtworkFileId,
            composite: useComposite.value,
        }),
        // variantSku is what gets sent as the upstream Artifi `sku` arg.
        // When the client-side coverage map resolved it, send the
        // Artifi-numeric form ("706882"). Otherwise send the legacy
        // shape and rely on backend resolution via master_sku/color_name.
        variantSku: clientResolvedVariantSku || variantSku.value,
        // Variant resolution wire parameters. Backend resolves
        // master_sku + color_name -> Artifi numeric variant SKU when
        // present, then uses that as the upstream sku. Master is the
        // CampaignProduct.product_item_number; color_name is the SanMar
        // color label.
        masterSku: productItemNumber.value,
        colorName: editorUi.selectedColorSku,
        variantBindings: variantBindings.value,
        // The placement gate at the request chokepoint (requestArtifiPreview):
        // no Artifi call for a product without a placement. The guard above
        // already returns null at zero; this is what the chokepoint checks.
        placementCount: (editor.decorations ?? []).length,
    };
});

// The preview request is held until coverage answers (a call sent earlier is
// spent on a maybe-unsupported style). That hold is a PENDING render: the
// Stage shimmers through it (see `loading` below) instead of flashing a bare
// blank. Only when everything else says "render" -- no placement, no color,
// etc. is not a wait.
const awaitingCoverage = computed(
    () => isArtifiCoveragePending() && liveCacheArgsIgnoringCoverage.value != null,
);
const liveCacheArgs = computed(() =>
    awaitingCoverage.value ? null : liveCacheArgsIgnoringCoverage.value,
);

// A color the Stage moved to by TICKING it (or as a side effect of an
// untick) makes no Artifi call FROM THE STAGE (see
// productEditorUiState.selectedColorIntent): the render manager
// (useStorefrontColorRenders) renders a ticked color at once (Jake,
// 2026-10-28) and the Stage shows the result through deferredStandIn below.
// Its render is shown if one already exists -- the preview cache is free --
// and otherwise the color's supplier photo stands in, with a "Rendering..."
// indicator while the render is pending.
const previewCache = useArtifiPreviewCacheStore();
const colorRenders = useStorefrontColorRendersStore();
const renderDeferredByIntent = computed(() =>
    ["tick", "auto"].includes(editorUi.selectedColorIntent),
);
const cacheArgs = computed(() => {
    const args = liveCacheArgs.value;
    if (!args) return null;
    if (renderDeferredByIntent.value && !previewCache.has(buildCacheKey(args))) {
        return null;
    }
    return args;
});

const {
    url: previewUrl,
    loading: renderLoading,
    error: previewError,
    colorwayState,
} = useArtifiPreviewCache(cacheArgs);

// What the server says about the bound colorway on the image now showing
// (null when the request names none). `applied` is silent apart from a
// screen-reader status; `pending` and `unavailable` say plainly that the
// image is the design's original colors -- never a broken tile, never a
// quiet lie.
const colorwayStateValue = computed(() => colorwayState?.value ?? null);
const colorwayNote = computed(() => {
    if (colorwayStateValue.value === COLORWAY_STATE.UNAVAILABLE) {
        return "Preview shows the original colors for this decoration.";
    }
    if (colorwayStateValue.value === COLORWAY_STATE.PENDING) {
        return "Applying the selected colors to the preview...";
    }
    return "";
});
// Every state where the UI is waiting on an Artifi preview: the render in
// flight, or the request held for the coverage answer.
const loading = computed(() => Boolean(renderLoading.value) || awaitingCoverage.value);

// What the Stage shows while a ticked color's render is deferred: that
// color's storefront render if the editor already made one this session,
// else its blank (the supplier photo for the color, else no image -- the
// note still shows). null when nothing is deferred.
// The render manager starts a ticked color's render at once, and a color it
// holds for the save debounce within SAVE_DEBOUNCE_MS (the platform setting);
// allow for that plus a render's duration (45s), then settle to
// the plain blank + note instead of shimmering forever for a color the manager
// skipped. Restarts whenever the color or the placement count changes.
const DEFERRED_WAIT_MS = 45_000;
const deferredWaitExpired = ref(false);
let deferredWaitTimer = null;
watch(
    () => [editorUi.selectedColorSku, (editor.decorations ?? []).length],
    () => {
        deferredWaitExpired.value = false;
        clearTimeout(deferredWaitTimer);
        deferredWaitTimer = setTimeout(() => {
            deferredWaitExpired.value = true;
        }, DEFERRED_WAIT_MS);
    },
    { immediate: true },
);
onBeforeUnmount(() => clearTimeout(deferredWaitTimer));

const deferredStandIn = computed(() => {
    if (!renderDeferredByIntent.value) return null;
    if (!liveCacheArgs.value || cacheArgs.value) return null;
    const sku = editorUi.selectedColorSku;
    const name =
        (editor.supplierMeta?.colors ?? []).find((c) => c.color_sku === sku)
            ?.name ?? sku;
    const render = colorRenders.renderFor?.(name);
    const url =
        render?.status === "ready" && render.url ? render.url : sanmarPreviewUrl.value;
    // Pending = a render is in flight (manager says so), or none has started
    // yet and we are still inside the bounded wait window. ready (with or
    // without a url), failed and partial are terminal: no shimmer.
    // 'idle' (card #338) means a render COULD start but nothing will start
    // it, so there is nothing to wait for: no shimmer, the explicit
    // "Render preview" action is offered instead.
    const status = render?.status;
    const state = colorRenders.standInState?.(name) ?? "none";
    const pending =
        status === "pending" ||
        (status === undefined && state !== "idle" && !deferredWaitExpired.value);
    return { url: url || null, pending, state };
});

function onRenderPreview() {
    colorRenders.renderColor?.(editorUi.selectedColorSku);
}

// Structured-404 fallback object surfaced to the template (Bug 3
// 2026-06-18). Returns null when there's no fallback-eligible error so
// the v-else-if chain falls through to the prior empty / overlay paths.
// Fallback-eligible: backend returned 404 with a recognized `hint`
// (sku_not_configured | artwork_not_registered) AND payload includes
// the structured fields.
//
// 2026-06-19 -- Artifi coverage gap UX: when the hint is
// sku_not_configured, rewrite the backend's technical
// "SKU '<x>' is not configured for view '<y>' in Artifi Website 445."
// message into a user-helpful product-state message. The technical
// message is preserved at .raw_message for support escalation use.
//
// 2026-08-14 (issue #2446 root-cause fix): appended a clarifying sentence
// naming the MOCKUP specifically as what's missing. This tile is driven by
// useArtifiPreviewCache, which fires off color/artwork selection alone and
// is decoupled from save by design (it can render before any save at all
// -- see "Previews render eagerly and independently" in CLAUDE.md), so the
// wording is scoped to the preview/mockup, never to a save having occurred.
function _coverageMessage(colorLabel) {
    if (colorLabel) {
        return (
            `Preview not yet available for this color (${colorLabel}) ` +
            "in Artifi. The product is still orderable in this color, " +
            "but no design preview is available yet. Only the mockup " +
            "image is affected."
        );
    }
    return (
        "Preview not yet available for this color in Artifi. " +
        "The product is still orderable, but no design preview is " +
        "available yet. Only the mockup image is affected."
    );
}

// 2026-07-13 -- render_failed (artifi_render_error) UX: Artifi's GetImage
// can 500 on an otherwise-configured view (e.g. an Artifi-side OpenTemplate
// misconfiguration). The backend maps that to a structured 404 with
// hint=render_failed and placeholder_url=null. Surface a graceful tile
// rather than letting the editor fall through to a bare console 404. The
// technical backend message is preserved at .raw_message for support use.
//
// 2026-08-14 (issue #2446 root-cause fix): appended the same "only the
// mockup is affected" clarification as _coverageMessage, for the same
// reason -- this tile can render pre-save.
function _renderFailedMessage() {
    return (
        "Preview isn't available for this placement view yet. The " +
        "product is still orderable; the design preview for this view " +
        "will appear once it's available. Only the mockup image is " +
        "affected."
    );
}

// 2026-08-12 -- budget_exceeded UX (issue #2446 slice): when the monthly
// Artifi render budget is reached, the backend returns a structured 404 with
// hint=budget_exceeded. This hint is different IN KIND from the others above:
// it is a TEMPORARY, system-wide condition that resolves on its own when the
// budget resets, and says nothing about this product, this color, or this
// artwork. The copy therefore avoids the product-state framing used by
// _coverageMessage/_renderFailedMessage and instead sets a "check back
// shortly" expectation while reassuring that the product is unaffected. The
// technical backend message is preserved at .raw_message for support use.
//
// 2026-08-14 (issue #2446 root-cause fix): "the product or your design"
// already covers the "your data is safe" reassurance for this hint. Kept
// as-is deliberately -- do NOT add the word "artwork" here (this hint is a
// system-wide budget condition, not an artwork-specific coverage gap; the
// existing test suite asserts the copy stays scoped to "temporary" +
// "product", not per-artwork framing).
function _budgetExceededMessage() {
    return (
        "Live previews are temporarily unavailable. This doesn't affect " +
        "the product or your design -- previews will return shortly."
    );
}

// 2026-08-14 (issue #2446 root-cause fix): artwork_not_registered previously
// fell through to the generic default branch below, which passed the raw
// backend message ("Artwork has no Artifi UniqueName yet.") straight to the
// user with no clarifying context -- unlike the other three hints, it never
// said anything about the product/mockup being otherwise fine. Give it the
// same "only the mockup is affected" treatment: keep the backend message
// (it's already written in plain language, no technical leak to hide) and
// append the clarification.
function _artworkNotRegisteredMessage(rawMessage) {
    const base = rawMessage || "Preview unavailable for this artwork.";
    return `${base} Only the mockup image is affected.`;
}

const previewFallback = computed(() => {
    const e = previewError?.value;
    if (!e || !e.hint) return null;
    if (
        e.hint !== "sku_not_configured" &&
        e.hint !== "artwork_not_registered" &&
        e.hint !== "render_failed" &&
        e.hint !== "budget_exceeded"
    ) {
        return null;
    }
    if (e.hint === "sku_not_configured") {
        // Rewrite the technical Artifi message into product-state copy.
        return {
            hint: e.hint,
            message: _coverageMessage(editorUi.selectedColorSku),
            raw_message: e.message || null,
            placeholder_url: e.placeholder_url || null,
        };
    }
    if (e.hint === "render_failed") {
        // Friendly copy; preserve the backend's technical message for
        // support escalation, mirroring the sku_not_configured branch.
        return {
            hint: e.hint,
            message: _renderFailedMessage(),
            raw_message: e.message || null,
            placeholder_url: e.placeholder_url || null,
        };
    }
    if (e.hint === "budget_exceeded") {
        // Temporary system-wide condition -- same tile contract, different
        // voice (see _budgetExceededMessage).
        return {
            hint: e.hint,
            message: _budgetExceededMessage(),
            raw_message: e.message || null,
            placeholder_url: e.placeholder_url || null,
        };
    }
    if (e.hint === "artwork_not_registered") {
        // Append the "only the mockup is affected" clarification (issue
        // #2446 root-cause fix). See _artworkNotRegisteredMessage.
        return {
            hint: e.hint,
            message: _artworkNotRegisteredMessage(e.message),
            raw_message: e.message || null,
            placeholder_url: e.placeholder_url || null,
        };
    }
    return {
        hint: e.hint,
        message: e.message || "Preview unavailable for this configuration.",
        placeholder_url: e.placeholder_url || null,
    };
});

// Unrenderable-colors blueprint 5.4 (Phase 6): "the canvas is explicit on
// removal". Only meaningful when the Stage is ALREADY showing the
// fallback tile (previewFallback non-null, i.e. the backend returned a
// sku_not_configured/render_failed hint for the active render) AND
// coverageFootnote has data for the CURRENTLY-SELECTED color -- this is
// the SAME text composition ColorsPanel's footnote list and
// ColorFilmstrip's tooltip use, so the three surfaces can never disagree
// about a given color's state. Returns null (no extra notice) when there
// is no footnote data yet -- the existing previewFallback.message above
// still renders on its own in that case (fail-open, unchanged behavior).
const colorRemovedNotice = computed(() => {
    if (!previewFallback.value) return null;
    const name = editorUi.selectedColorSku;
    if (!name) return null;
    const footnote = coverageFootnote(name);
    if (!footnote) return null;
    if (footnote.allFail) {
        return `${name} -- no preview available for this color`;
    }
    return `${name} -- no preview on ${footnote.failingViews.join(", ")}`;
});

const previewAlt = computed(() => {
    const v = activeVariant.value;
    if (!v) return "Product preview";
    return `Preview for ${variantSku.value || v.kind || "product"}`;
});

// Dashed overlay gate: a placement is ACTIVE (selected, or the first-
// placement fallback) AND its routed variant has no artwork. Independent
// of cacheArgs nullness because we want the overlay even when
// productItemNumber isn't loaded yet. Tier 1 activePreview: keying off
// the effective placement means a fresh load on an artwork-less
// placement honestly shows its zone awaiting artwork instead of a
// generic empty state.
const showDashedOverlay = computed(() => {
    const p = activePlacement.value;
    if (!p) return false;
    const variants = p.variants ?? [];
    if (!variants.length) return true;
    // An UNROUTED color is not an "awaiting artwork" zone. Before the
    // 2026-08-23 resolver-miss fix this branch carried the same
    // `variants[0]` fallback activeVariant did, so a miss silently
    // reported the anchor variant's artwork state. Now the miss has its
    // own explicit surface (`unresolvedColor`), and drawing a dashed zone
    // underneath it would say two different things about one placement --
    // "add artwork here" AND "this color routes nowhere". Only the second
    // is true.
    if (unresolvedColor.value) return false;
    let v = null;
    if (editorUi.selectedColorSku) {
        const placementId = p.id ?? p._tempId;
        v = resolveVariantForColor(placementId, editorUi.selectedColorSku);
    }
    if (!v) v = variants[0];
    return !v?.artwork_file_id;
});

// Catalog-driven placement-zone boxes. Fetches once per supplier SKU;
// stays in sync as the user changes products. The composable returns
// an empty array on unknown SKU / fetch error -- the overlay then
// hides instead of falling back to a synthetic position (the bug Phase
// F F3.c fixes).
const { boxes: catalogBoxes, fetch: fetchPlacementBoxes } =
    usePlacementZoneBoxes();

watch(
    productItemNumber,
    (sku) => {
        if (sku) {
            fetchPlacementBoxes(sku);
        } else {
            // No SKU yet -- clear via a no-op fetch with a falsy value.
            // The composable resets boxes to [] on invalid input.
            fetchPlacementBoxes(null);
        }
    },
    { immediate: true },
);

const overlayBox = computed(() => {
    const p = activePlacement.value;
    if (!p) return null;
    // Resolve the zone SLUG, matching productEditorUiState.selectedView.
    // After the P6 PR-A Placement-substrate rename, `placement_id` is the
    // placement PRIMARY KEY (a number), NOT a zone slug -- reading it here
    // made resolvePlacementBox look up a box by the numeric PK and the
    // dashed placement overlay never drew on the right zone. Read the real
    // zone slug threaded onto the row (`placement_zone_slug`); the
    // placement / zone / zone_code aliases stay for fixtures. Never read
    // `placement_id` as a zone.
    const zone =
        p.placement_zone_slug ||
        p.placement ||
        p.zone ||
        p.zone_code ||
        null;
    return resolvePlacementBox({
        zoneCode: zone,
        selectedView: editorUi.selectedView,
        boxes: catalogBoxes.value,
        // SKU-aware: the Stage's live Artifi views make the overlay hide on
        // a zone that isn't a real placement for this product.
        skuViews: editorUi.skuViews,
    });
});

// SVG stroke widths are in user units of the viewBox (== pixel-space of
// the product photo). The photo is ~1200-1800 px tall on the products
// we have data for; a 6 px stroke reads as roughly 0.4% of the height
// -- close to the old percent-space "0.8" stroke at viewBox=100.
// Computed off the box so a photo at a wildly different resolution
// still renders a readable dashed line.
const _overlayScale = computed(() => {
    const b = overlayBox.value;
    if (!b) return 1;
    return Math.max(b.image_width_px, b.image_height_px) / 100;
});
const overlayStrokeWidth = computed(() => 0.8 * _overlayScale.value);
const overlayStrokeDasharray = computed(() => {
    const s = _overlayScale.value;
    return `${2 * s},${1.5 * s}`;
});

// Used by the empty-state guard to avoid showing both "select or upload"
// copy AND the dashed overlay simultaneously.
const hasAnyArtwork = computed(() => {
    const decos = editor.decorations ?? [];
    for (const deco of decos) {
        const variants = deco.variants ?? [];
        for (const v of variants) {
            if (v.artwork_file_id) return true;
        }
    }
    return false;
});

// True when the product has no decoration placements yet. Used to gate
// the SanMar swatch preview path (no Artifi call when no placements).
const noPlacements = computed(() => (editor.decorations ?? []).length === 0);

// The selected color's BLANK (its supplier photo, else a tinted silhouette)
// claims the canvas when there is nothing to render -- no placements -- or
// when the render FAILED with no explanatory tile to show (a timeout, a
// network drop, a 5xx). Jake 2026-10-08: the distributor sees the garment
// they picked, never an empty canvas. A failure the fallback tile explains
// keeps the tile (it outranks these branches).
const showColorBlank = computed(
    () =>
        noPlacements.value ||
        (Boolean(previewError?.value) && !loading.value && !previewUrl.value),
);

// ---------------------------------------------------------------------------
// APPROXIMATE PLACEMENT PREVIEW (GH #2874).
//
// Sleeve / leg / hip zones are OFFERED by the placement wizard, but Artifi
// exposes a camera for them on almost no product -- 3 of 2,862 SKUs carry a
// Sleeve view (verified against docs/integrations/artifi/"SanMar Location
// Sheet - Enum List.xlsx"). When a placement's zone maps to no configured
// view the resolver does not fail: resolveViewCodeForSku substitutes the
// SKU's FRONT-biased default, which on most SKUs is the very Left Chest view
// a chest placement already claimed. The backend then hits
// placement_view_collision and DROPS one placement -- the distributor placed
// two decorations, sees one, and the image is presented as the truth.
//
// Jake's decision (2026-08-26): "Offer them and render our own SVG
// approximation with a clear 'approximate placement' indication." The zone
// stays selectable; we stop pretending Artifi rendered it. What is eliminated
// is the CONFIDENTLY WRONG preview, not the placement.
//
// FAIL-OPEN is load-bearing. isZoneValidForSku's documented contract is that
// live skuViews are AUTHORITATIVE, but UNAVAILABLE skuViews (not fetched yet,
// fetch error, no Artifi creds) mean NO authority -- every zone is treated as
// valid. An "approximate" label must never appear merely because a fetch was
// slow, so this whole path is inert until the views land.
// ---------------------------------------------------------------------------

// Every live placement on this product, with its zone slug resolved the same
// way overlayBox / artworkCompatibleWithView resolve it. Never read
// `placement_id` as a zone -- after the P6 rename it is the numeric PK.
//
// These three are FUNCTION DECLARATIONS, not `const` computeds, and that is
// deliberate. `cacheArgs` (defined far above) has to consult the gate in
// order to suppress the Artifi call, and a `const` computed declared here
// would sit in the temporal dead zone when cacheArgs first evaluates during
// setup -- "Cannot access 'showApproximatePreview' before initialization",
// which took out 37 Stage.spec tests when this was first written that way.
// Function declarations hoist, so the ordering hazard disappears rather than
// being managed. They read reactive store state directly, so Vue still tracks
// the dependencies of whichever computed calls them.
function allPlacementZones() {
    return (editor.decorations ?? []).map((p) => ({
        placement: p,
        zone:
            p.placement_zone_slug ||
            p.placement ||
            p.zone ||
            p.zone_code ||
            null,
    }));
}

// The placements whose zone has NO valid view on this SKU. Empty when there
// is no authority (fail open) -- isZoneValidForSku returns true for every
// zone in that case, so nothing lands here.
function unrenderablePlacements() {
    return allPlacementZones().filter(
        ({ zone }) => zone && !isZoneValidForSku(zone, editorUi.skuViews),
    );
}

// The gate for the whole approximate path. ANY unrenderable placement makes
// the WHOLE preview approximate, not just that one mark: Artifi renders one
// image per view, so a composite that silently omits the sleeve placement is
// the same confidently-wrong claim the fix exists to remove. Drawing every
// mark ourselves is the honest answer -- and it is the only way the mixed
// case (a renderable chest PLUS an unrenderable sleeve) shows BOTH
// decorations, which is precisely what the bug report describes.
//
// Requires a selected color so the silhouette has a tint, mirroring the
// existing sanmar-silhouette branch's gate.
function isApproximatePreview() {
    if ((editor.decorations ?? []).length === 0) return false;
    if (!editorUi.selectedColorSku) return false;
    return unrenderablePlacements().length > 0;
}

const showApproximatePreview = computed(() => isApproximatePreview());

// The zone slugs the approximation cannot even place -- the silhouette has no
// authored hotspot for them. Used only for copy; the mark still renders
// centred so the decoration is never invisible.
const approximateSilhouette = computed(() => silhouetteClass.value);

// One overlay per placement, positioned on the silhouette's hotspot for its
// zone at TRUE RELATIVE SCALE where we have the geometry, and centred as a
// rough schematic where we do not. Mirrors GarmentPreview.vue's mapping (the
// reference implementation) so the two surfaces agree about where a zone is.
//
// A placement with no authored hotspot still gets a mark -- it is honestly
// labelled approximate either way, and dropping it would reproduce the very
// "you placed two, you see one" bug this fixes.
const approximateMarks = computed(() =>
    isApproximatePreview() ? placementMarks(approximateSilhouette.value) : [],
);

// One mark per placement on silhouette ``sil``: the approximation above and
// the #243 zone diagram / open canvas share it, so they agree about where a
// zone is. ``box`` is the same geometry in numbers (Stage percent) for the
// SVG zone overlay and the canvas.
//
// ``photo``: calibrate the boxes to a supplier PHOTO (it fills its frame)
// instead of the silhouette drawing (it sits inside a pad). Null where the
// silhouette has no measured outline: the caller then keeps the drawing boxes.
function placementMarks(sil, { photo = false } = {}) {
    const [vbW, vbH] = viewBoxFor(sil);
    const geo = _silhouetteArtGeometry(vbW, vbH);
    const out = [];
    let roughIndex = 0;
    for (const { placement, zone } of allPlacementZones()) {
        const hot = zone ? hotspotFor(sil, zone) : null;
        // The ZONE's printable envelope -- deliberately NOT the decoration's
        // own dimensions. overlayUnitsFor derives units-per-inch by dividing
        // the hotspot width by the ZONE width, so passing the decoration size
        // here makes every mark render at exactly full-zone scale: a 1x1 logo
        // and a 4x4 logo would look identical, destroying the one thing a
        // placement preview exists to show. Pinned by the "scales a mark by
        // the DECORATION size against the ZONE envelope" test.
        //
        // hotspotFor and ZONE_INCHES_BY_SLUG are keyed INDEPENDENTLY, so a
        // zone can have a hotspot and NO inches (verified:
        // baseballCap/front_crown). zoneInchesFor returns null there, and
        // destructuring null throws -- which would blank the whole editor
        // rather than degrade. Treat "no inches" as "no usable geometry" and
        // fall through to the rough mark.
        const zoneInches = zone ? zoneInchesFor({ slug: zone }) : null;
        let units;
        if (hot && zoneInches) {
            const [zw, zh] = zoneInches;
            // The decoration's real size when we have it; otherwise assume it
            // fills its zone, which is the honest default for a mark whose
            // dimensions have not been set yet.
            const widthIn = Number(placement.width_in) > 0
                ? Number(placement.width_in)
                : zw;
            const heightIn = Number(placement.height_in) > 0
                ? Number(placement.height_in)
                : zh;
            units = overlayUnitsFor({
                hot,
                zoneWidthIn: zw,
                zoneHeightIn: zh,
                widthIn,
                heightIn,
            });
        } else {
            // ROUGH: no authored hotspot for this (silhouette, zone). Stagger
            // successive rough marks so two of them never sit exactly on top
            // of each other and read as one decoration.
            const w = vbW * 0.24;
            const h = vbH * 0.16;
            units = {
                cx: vbW / 2,
                cy: vbH * (0.34 + 0.16 * roughIndex),
                w,
                h,
            };
            roughIndex += 1;
        }
        const ux = units.cx - units.w / 2;
        const uy = units.cy - units.h / 2;
        const box = photo
            ? photoBoxFor(sil, ux, uy, units.w, units.h)
            : _mapBoxToStage(geo, ux, uy, units.w, units.h);
        if (!box) return null;
        out.push({
            key: String(placement.id ?? placement._tempId ?? zone ?? out.length),
            zone,
            zoneLabel: _zoneLabel(zone) || "placement",
            box,
            exact: Boolean(hot && zoneInches),
            artworkUrl: _approximateArtworkUrl(placement),
            initial: _approximateInitial(placement, zone),
            style: {
                left: `${box.leftPct}%`,
                top: `${box.topPct}%`,
                width: `${box.wPct}%`,
                height: `${box.hPct}%`,
            },
        });
    }
    return out;
}

// ---------------------------------------------------------------------------
// PREVIEW FALLBACKS (card #243, https://trello.com/c/jFoJk0j5).
//
// Where Artifi cannot draw the preview the Stage offers three visual-only
// alternatives (ArtifiFallbackPreview): the distributor's own image for the
// selected color (uploaded in place -- Jake 2026-10-20), a zone diagram, and
// an open canvas. Two triggers:
//   * showStyleFallback -- the STYLE is unsupported (no request is made).
//   * showHintFallback  -- a render for this color answered
//     sku_not_configured or render_failed (inside the existing tile).
// The upload is keyed by the selected color: storefront_images keys are the
// selected color values (color name == color_sku for SanMar and S&S), and
// the server accepts only a selected color.
// ---------------------------------------------------------------------------
// The two alternatives named here exist only while the SuperAdmin switch is
// on (default OFF), so the copy follows it.
const fallbackTools = usePreviewFallbackTools();
const styleUnsupportedMessage = computed(() =>
    fallbackTools.toolsEnabled.value
        ? "Automatic mockup previews aren't available for this style. You can " +
          "still sell it in every color -- show shoppers your own image, a zone " +
          "diagram or an open-canvas mockup instead."
        : "Automatic mockup previews aren't available for this style. You can " +
          "still sell it in every color -- shoppers see the supplier photo, or " +
          "your own image if you upload one.",
);

const _FALLBACK_HINTS = new Set(["sku_not_configured", "render_failed"]);

const showStyleFallback = computed(
    () => Boolean(editorUi.selectedColorSku) && isArtifiStyleUnsupported(),
);

const showHintFallback = computed(
    () =>
        Boolean(editorUi.selectedColorSku) &&
        _FALLBACK_HINTS.has(previewFallback.value?.hint),
);

// While the fallback options (supplier photo / zone diagram / open canvas)
// are on the Stage, the tile is sized by its content, not by the editor's
// fixed stage height: that cap left the preview about 190x220px.
const fallbackOptionsShown = computed(
    () => showStyleFallback.value || showHintFallback.value,
);

const fallbackProps = computed(() => {
    const color = editorUi.selectedColorSku || "";
    return {
        colorName: color,
        colorLabel: selectedColorLabel.value || color,
        colorHex: selectedColorForSilhouette.value?.hex ?? null,
        silhouette: silhouetteClass.value,
        blankUrl: sanmarPreviewUrl.value,
        marks: placementMarks(silhouetteClass.value),
        // Over a supplier photo the zone diagram uses boxes calibrated to it.
        photoMarks: sanmarPreviewUrl.value
            ? placementMarks(silhouetteClass.value, { photo: true })
            : null,
        customPreviewUrl: colorUploadUrl(
            editor.getEffectiveAttribute?.("storefront_images"),
            color,
        ),
        status: editor.colorPreviewStatus?.(color) ?? {
            state: "idle",
            message: null,
        },
        toolsEnabled: fallbackTools.toolsEnabled.value,
    };
});

function onFallbackUpload(file, filename) {
    const color = editorUi.selectedColorSku;
    if (!color) return;
    editor.uploadColorPreview?.(color, file, filename);
}

// The selected color's uploaded image is shown here instead of its render, whether or
// not Artifi can render it. The fallback tiles show the same image inside
// themselves, so this claims only the other states.
const uploadedPreviewUrl = computed(() => fallbackProps.value.customPreviewUrl);
const showUploadedImage = computed(
    () =>
        Boolean(uploadedPreviewUrl.value) &&
        !noColorsSelected.value &&
        !unresolvedColor.value &&
        !fallbackOptionsShown.value,
);
const showColorImageStrip = computed(
    () => Boolean(editorUi.selectedColorSku) && !noColorsSelected.value,
);

// Remove brings the ThreadFlow render back (Jake, 2026-10-28). The server
// deletes the upload (an emptied color loses its key, which reads "never
// curated"); the render that already landed for the color is then chosen
// again, exactly as the render manager would have chosen it, so the listing,
// the product page and the cart show it without a re-render.
async function onFallbackRemove() {
    const color = editorUi.selectedColorSku;
    if (!color) return;
    const removed = await editor.removeColorPreview?.(color);
    if (!removed || editorUi.selectedColorSku !== color) return;
    const render = colorRenders.renderFor?.(selectedColorLabel.value);
    if (render?.status !== "ready" || !render.url) return;
    const current = editor.getEffectiveAttribute?.("storefront_images") ?? {};
    const next = includeColorRender(current, selectedColorLabel.value, render.url);
    if (next !== current) editor.updateField?.("storefront_images", next);
}

// The honest label. Voice matches the existing previewFallback copy: states
// the limitation, does not blame the user, does not read as an error. The
// distributor's decorations ARE going on the garment -- only our picture of
// where is inexact.
const approximateNotice = computed(() => {
    if (!isApproximatePreview()) return null;
    const zones = unrenderablePlacements()
        .map(({ zone }) => _zoneLabel(zone))
        .filter(Boolean);
    // Name the affected zones. An earlier draft said "every placement on this
    // product" for the plural case, which is FALSE whenever a renderable
    // placement sits alongside an unrenderable one (the reported case) -- and
    // a preview that overstates its own limitation is a smaller version of
    // the same dishonesty this change removes. Falls back to the vaguer
    // phrasing only when no zone resolved to a readable name.
    const base =
        "Approximate placement -- our preview supplier has no camera for " +
        (zones.length === 0
            ? "this placement on this product"
            : `the ${_joinZones(zones)} on this product`);
    return `${base}, so this is our own sketch of where the decoration sits. Positions are indicative, not exact. Only the preview image is affected -- the decorations themselves are unchanged.`;
});

// "a", "a and b", "a, b and c" -- a plain English list, deduped so two
// placements on the same zone do not read as "the right sleeve and the right
// sleeve".
function _joinZones(zones) {
    const uniq = [...new Set(zones)];
    if (uniq.length === 1) return uniq[0];
    if (uniq.length === 2) return `${uniq[0]} and ${uniq[1]}`;
    return `${uniq.slice(0, -1).join(", ")} and ${uniq[uniq.length - 1]}`;
}

// A readable zone name from its slug ("right_sleeve" -> "right sleeve").
function _zoneLabel(zone) {
    if (typeof zone !== "string" || !zone) return null;
    return zone.replace(/_/g, " ").trim().toLowerCase() || null;
}

// Best-effort art for a mark. The variant the user would actually see wins;
// falls back to nothing (the caller renders an initial instead).
function _approximateArtworkUrl(placement) {
    const variants = placement.variants ?? [];
    let v = null;
    if (editorUi.selectedColorSku) {
        const placementId = placement.id ?? placement._tempId;
        v = resolveVariantForColor(placementId, editorUi.selectedColorSku);
    }
    if (!v) v = variants[0] ?? null;
    if (!v) return null;
    if (v.artwork_thumbnail_url || v.artwork_preview_url) {
        return v.artwork_thumbnail_url || v.artwork_preview_url;
    }
    // The variant row carries no image of its own: resolve it from the
    // artwork repository with VariantCard's precedence (card #243 -- the
    // zone diagram and open canvas draw this art, and the canvas exports
    // it). A stitch file's image_url is source bytes, never an image.
    const art = artworks.getArtworkForVariant?.(v) ?? null;
    if (art?.thumbnail_url) return art.thumbnail_url;
    const shown = previewDisplay(v.preview ?? art?.preview);
    if (shown.kind === "ready") return shown.previewUrl;
    const imageUrl = art?.image_url;
    if (
        imageUrl &&
        !isEmbroiderySource(String(imageUrl).split("?")[0]) &&
        !isEmbroiderySource(art?.filename)
    ) {
        return imageUrl;
    }
    return null;
}

function _approximateInitial(placement, zone) {
    const label = _zoneLabel(zone) || "";
    const first = label.charAt(0).toUpperCase();
    return first || "D";
}

// The garment-art placement geometry inside the stage. Mirrors how
// GarmentSilhouette renders (inset by its 16% pad, then contain-fit +
// centred), so an overlay in viewBox coordinates lands ON the art rather
// than floating in the pad / letterbox whitespace.
//
// This duplicates GarmentPreview.vue's private silhouetteArtGeometry rather
// than importing it, because that function is not exported and
// GarmentPreview.vue is owned by a parallel work stream this run must not
// edit. The duplication is deliberate and bounded: if a third caller ever
// needs it, promote it into placementPreviewGeometry.js in one move instead
// of copying it again.
const _STAGE_ASPECT = 6 / 7;
const _SILHOUETTE_PAD = 0.16;

function _silhouetteArtGeometry(vbW, vbH) {
    const sw = _STAGE_ASPECT;
    const sh = 1;
    const pw = (1 - 2 * _SILHOUETTE_PAD) * sw;
    const ph = (1 - 2 * _SILHOUETTE_PAD) * sh;
    const s = Math.min(pw / vbW, ph / vbH);
    const aw = s * vbW;
    const ah = s * vbH;
    return {
        vbW,
        vbH,
        aw,
        ah,
        xOrigin: _SILHOUETTE_PAD * sw + (pw - aw) / 2,
        yOrigin: _SILHOUETTE_PAD * sh + (ph - ah) / 2,
        sw,
        sh,
    };
}

function _mapBoxToStage(g, ux, uy, uw, uh) {
    return {
        leftPct: ((g.xOrigin + (ux / g.vbW) * g.aw) / g.sw) * 100,
        topPct: ((g.yOrigin + (uy / g.vbH) * g.ah) / g.sh) * 100,
        wPct: (((uw / g.vbW) * g.aw) / g.sw) * 100,
        hPct: (((uh / g.vbH) * g.ah) / g.sh) * 100,
    };
}

// SanMar product photo for the currently-selected color. Used as the
// preview image when noPlacements is true -- shows the actual garment photo
// from the SanMar catalog instead of calling Artifi for a garment composite
// that would error (no placement context to build around).
//
// Source: supplierMeta.image_urls_by_color -- the per-color photo pool the
// product_meta endpoint already resolves correctly (getMediaContent-first,
// EPDD date-shard-folder FLAT fallback per color). We must NOT reconstruct a
// URL from the bare COLOR_PRODUCT_IMAGE filename: joining it to the bare
// mresjpg CDN root 200s-but-REDIRECTS to SanMar's ImageNotAvailable.jpg
// placeholder (the 2026-07 date-folder fix, see
// api/src/endpoints/sanmar_feed.py). The map is keyed by the same color
// string as each color row's color_sku.
//
// Returns a REAL product photo or null -- never the swatch chip. When a
// color has no product photo (genuinely photoless), null flows to the
// GarmentSilhouette fallback in the template. The tiny /swatch/*.gif fabric
// chip is a color chip, not a garment preview, and must never render as the
// stage image (2026-08 no-placement-preview fix).
const sanmarPreviewUrl = computed(() => {
    const sku = editorUi.selectedColorSku;
    if (!sku) return null;
    const byColor = editor.supplierMeta?.image_urls_by_color;
    // Scope the pool to the selected color's entry, then reuse the shared
    // pickPreviewImage heuristic to prefer a flat-front shot within it.
    const urls = byColor ? byColor[sku] : null;
    if (Array.isArray(urls) && urls.length > 0) {
        const photo = pickPreviewImage({ [sku]: urls });
        if (photo) return photo;
    }
    return null;
});

// Tinted-silhouette fallback for a genuinely-photoless color (no product
// photo in image_urls_by_color, and no getMediaContent either). Shows a
// garment-shaped placeholder tinted to the selected color -- never the
// swatch chip. Garment class derives from the EPDD category/subcategory the
// product-meta endpoint surfaces (silhouetteFor -> 'generic' default); the
// tint hex comes from the selected color's supplierMeta row.
const silhouetteClass = computed(() =>
    silhouetteFor(
        editor.supplierMeta?.category,
        editor.supplierMeta?.subcategory,
    ),
);

const selectedColorForSilhouette = computed(() => {
    const sku = editorUi.selectedColorSku;
    const all = editor.supplierMeta?.colors ?? [];
    const row = all.find((c) => c.color_sku === sku);
    // hex may be null -- GarmentSilhouette supplies its own neutral default.
    return { name: sku || "", hex: row?.hex ?? null };
});

// Blank-garment preview (no decoration placements + no color selected):
// auto-pick the first color from selected_color_skus so the bare-render
// path fires through useArtifiPreviewCache (artworkFileId=null) and the
// user sees the Artifi-rendered bare garment instead of an empty state.
// Revised 2026-09-01 (Jake): "the editor must never render a preview for
// a color that is not enabled." The watcher is therefore a CONTINUOUS
// re-validation of the selection, not a one-shot initial pick. It runs
// whenever the enabled list or the selection changes, and:
//   - Leaves the selection alone while it is present AND still enabled.
//   - Re-picks the first enabled color when the selection is absent OR no
//     longer a member of the enabled list (the user deselected the color
//     they were viewing; a selection leaked in from a previously-open
//     product through these singleton stores).
//   - Clears to null when nothing is enabled, restoring the empty state.
//   - Stays out of the SA-without-impersonation guard state entirely (the
//     backend would 403 the preview anyway and there is no tenant scope
//     for the pick to belong to).
// The composable's debounce coalesces the resulting first render with
// any subsequent user-driven color clicks, so we don't waste an Artifi
// call when the user immediately picks something different.
const availableColorSkus = computed(() => {
    const raw = editor.getEffectiveAttribute?.("selected_color_skus") ?? [];
    if (!Array.isArray(raw)) return [];
    return raw;
});

// "No colors are enabled" and "the product has not loaded yet" are DIFFERENT
// facts that availableColorSkus alone cannot tell apart -- an unloaded CP and
// a CP whose colors were all removed both read as []. That distinction is
// load-bearing for the re-validation watcher below: clearing a selection is
// right in the second case and WRONG in the first, where it would wipe a
// legitimate selection during the window between mount and the server load
// landing (getEffectiveAttribute returns undefined until serverState arrives).
//
// So read the raw attribute and treat "an actual array" as the authority
// signal. A non-array (undefined / not yet loaded) means we have no basis to
// judge the selection, and the watcher leaves it alone.
const enabledColorsAreKnown = computed(() =>
    Array.isArray(editor.getEffectiveAttribute?.("selected_color_skus")),
);

// The product has NO colors selected: the colors are KNOWN (loaded) and the
// list is empty. Claims the Stage for stage-empty ahead of every other state
// (Jake, 2026-09-28 -- see the template's precedence chain). An unloaded CP
// is deliberately not this state, for the reason enabledColorsAreKnown gives.
const noColorsSelected = computed(
    () => enabledColorsAreKnown.value && availableColorSkus.value.length === 0,
);

watch(
    [
        availableColorSkus,
        () => editorUi.selectedColorSku,
        isSuperAdminWithoutImpersonation,
        enabledColorsAreKnown,
    ],
    ([colors, selected, saBlocked, colorsKnown]) => {
        if (saBlocked) return;
        if (typeof editorUi.setSelectedColor !== "function") return;
        // Until the CP's colors have actually loaded we cannot say whether
        // the selection is valid, so make no judgement at all. Without this
        // the mount-to-load window looks identical to "all colors removed"
        // and would clear a legitimate selection out from under the user.
        if (!colorsKnown) return;
        // The selection is honored only while it is STILL ENABLED. Testing
        // mere presence (`if (selected) return`) was the stale-color bug:
        // nothing anywhere clears or re-validates selectedColorSku, so once
        // the previewed color left selected_color_skus the Stage kept
        // rendering it forever. Membership is the whole fix -- a valid
        // selection is still never clobbered (the no-clobber contract), an
        // invalid one is corrected here.
        if (selected && colors.includes(selected)) return;
        // Nothing is enabled: there is no color-specific preview to show, so
        // clear rather than leave a stale value behind. Leaving it kept the
        // tinted-silhouette branch winning (`noPlacements &&
        // editorUi.selectedColorSku`) and suppressed the "Select a color to
        // preview." empty state after the user removed all colors.
        if (!colors.length) {
            if (selected) editorUi.setSelectedColor(null);
            return;
        }
        // "Default color" MEANS the first enabled color (Jake, 2026-09-01):
        // position 0 of selected_color_skus, no stored default and no schema
        // change. availableColorSkus reads through getEffectiveAttribute, so
        // this sees the user's unsaved edits and colors[0] is enabled by
        // construction.
        const first = colors[0];
        if (!first || typeof first !== "string") return;
        if (first === selected) return;
        // 'open' when nothing was selected yet (the product just opened: the
        // Stage shows it, as it always has); 'auto' when the selected color
        // went away -- a side effect of unticking, which must not render.
        editorUi.setSelectedColor(first, selected ? "auto" : "open");
    },
    { immediate: true },
);

// Empty-state copy is context-sensitive:
//   - "Select a color to preview." when no color is picked yet (whether
//     because none is available on the CP or because the SA-no-
//     impersonation gate suppressed auto-pick). This is the right
//     steering message before a color is in play -- the blank-garment
//     render is the next thing the user should see.
//   - "Select or upload artwork to preview." when a color IS selected
//     but the bare render hasn't surfaced one (e.g. coverage gap with
//     no fallback URL) -- steers the user toward the decoration
//     workflow.
const emptyStateMessage = computed(() => {
    if (noColorsSelected.value || !editorUi.selectedColorSku) {
        return "Select a color to preview.";
    }
    return "Select or upload artwork to preview.";
});

// Amazon-style hover-zoom lens over the preview image. The lens is only
// wired to the <img> that renders when previewUrl is present, so it can
// never activate over the shimmer / fallback / empty states. useHoverZoom
// owns the letterbox-aware geometry and the low-res zoom cap; the template
// binds lensStyle to the loupe and the handlers to the image. `enabled`
// gates on previewUrl so a lingering handler can't fire mid-transition.
const stageImageEl = ref(null);
const {
    lensStyle,
    onMouseMove,
    onMouseLeave,
    onImageLoad,
} = useHoverZoom({
    imageEl: stageImageEl,
    imageUrl: previewUrl,
    enabled: computed(() => Boolean(previewUrl.value)),
});
</script>

<style scoped>
.stage-root {
    height: 100%;
    background: var(--tf-bg-stage, #f8fafc);
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
}

/* Fallback options showing: grow with the content. The editor sets the tile's
   height on this same element (.product-editor-stage, clamp(...)); the doubled
   class outranks it and its narrow-width media override. */
.stage-root.stage-root--content-sized {
    height: auto;
    min-height: 0;
}

.stage-preview-area {
    position: relative;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

.stage-shimmer {
    width: 240px;
    height: 240px;
    border-radius: 8px;
}

.stage-deferred {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    max-width: 100%;
    max-height: 100%;
    min-height: 0;
}

.stage-deferred .stage-image {
    min-height: 0;
}

.stage-render-preview {
    padding: 6px 14px;
    font-size: 13px;
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: 6px;
    background: var(--tf-surface, #fff);
    cursor: pointer;
}

.stage-deferred-rendering {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.stage-deferred-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--tf-border, #d1d5db);
    border-top-color: var(--tf-text-muted, #6b7280);
    border-radius: 50%;
    animation: stage-deferred-spin 0.8s linear infinite;
}

@keyframes stage-deferred-spin {
    to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
    .stage-deferred-spinner {
        animation: none;
    }
}

.stage-deferred-note {
    margin: 0;
    font-size: 13px;
    color: var(--tf-text-muted, #6b7280);
}

.stage-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.stage-empty {
    color: var(--tf-text-muted, #6b7280);
    font-size: 14px;
    text-align: center;
}

/* DST/EMB stitch-file preview tiles (PR-6). Same footprint as the
   shimmer/image; the pending tile reuses the shimmer block above a
   caption, the failed tile shows the honest re-upload copy. */
.stage-dst-pending,
.stage-dst-failed {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    padding: 24px;
    max-width: 360px;
}

.stage-dst-text {
    margin: 0;
    color: var(--tf-text-muted, #6b7280);
    font-size: 14px;
}

.stage-dst-failed .stage-dst-text {
    color: #8a1c1c;
}

/* Approximate placement preview (GH #2874). Deliberately shares the
   fallback tile's voice and spacing -- it is the same honest-degradation
   seam PR #2852 established, not a new visual language. The canvas keeps
   the 6/7 aspect the overlay math (_silhouetteArtGeometry) assumes, so a
   mark's percentage coordinates land on the garment art. */
.stage-approximate {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    max-width: 100%;
    max-height: 100%;
    min-height: 0;
    color: var(--tf-text-muted, #6b7280);
    font-size: 14px;
    text-align: center;
}
/* A DEFINITE size: the canvas used to be height:100% of this auto-height
   flex column, which collapsed it (and the silhouette and marks inside it) to
   0x0. The height is definite (viewport-relative so it leaves room for the
   notice under it inside the stage) and the 6/7 aspect-ratio derives the
   width the overlay math assumes. */
.stage-approximate-canvas {
    position: relative;
    flex: 0 0 auto;
    height: clamp(180px, 32vh, 490px);
    aspect-ratio: 6 / 7;
    max-width: 100%;
}
.stage-approximate-silhouette {
    width: 100%;
    height: 100%;
}
.stage-approximate-mark {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    pointer-events: none;
}
.stage-approximate-art {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
/* No artwork bound yet: a neutral schematic block so the placement is
   still visibly THERE. Dashed to read as indicative, matching the dashed
   zone overlay's grammar elsewhere on this Stage. */
.stage-approximate-art-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border: 1px dashed var(--tf-border-strong, #9ca3af);
    border-radius: 2px;
    background: var(--tf-overlay-faint, rgba(31, 41, 55, 0.06));
    color: var(--tf-text-muted, #6b7280);
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
}
.stage-approximate-notice {
    margin: 0;
    max-width: 420px;
    flex: 0 0 auto;
}

.stage-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: var(--tf-text-muted, #6b7280);
    font-size: 14px;
    text-align: center;
    padding: 24px;
}
.stage-fallback--options {
    width: 100%;
    min-height: 0;
    justify-content: safe center;
    padding: 12px;
    gap: 8px;
}
.stage-fallback-img {
    max-width: 60%;
    max-height: 60%;
    object-fit: contain;
    opacity: 0.65;
}
.stage-fallback-text {
    margin: 0;
    max-width: 360px;
}
/* Unrenderable-colors blueprint 5.4 (Phase 6): explicit removal notice,
   reusing coverageFootnote's text composition. Slightly more prominent
   than the generic fallback text above it (which stays visible too) so
   the specific color-state statement stands out. */
.stage-canvas-removed-notice {
    margin: 0;
    max-width: 360px;
    font-weight: 600;
    color: var(--tf-text-primary, #1f2937);
}
/* Unrouted garment color (fix, 2026-08-23). Same centered footprint as
   the fallback tile above so the canvas keeps one visual grammar. The
   title carries the weight (it names the color); the body and remedy sit
   at muted weight beneath it. Deliberately NOT styled as an error -- an
   unrouted color is a normal configuration gap a distributor hits while
   building, and red-flagging it would train them to ignore it. */
.stage-unresolved-color {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    padding: 24px;
    max-width: 360px;
}
.stage-unresolved-color-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--tf-text-primary, #1f2937);
}
.stage-unresolved-color-body,
.stage-unresolved-color-remedy {
    margin: 0;
    font-size: 13px;
    color: var(--tf-text-muted, #6b7280);
}
/* The server's note on the bound colorway (pending / unavailable): a quiet
   caption along the bottom edge, over the still-visible image. */
.stage-colorway-note {
    position: absolute;
    left: 50%;
    bottom: 8px;
    transform: translateX(-50%);
    max-width: calc(100% - 16px);
    margin: 0;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    text-align: center;
    color: var(--tf-text-muted, #6b7280);
    background: rgba(255, 255, 255, 0.92);
}
/* Screen-reader status for an applied colorway; visually hidden. */
.stage-colorway-applied {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
.stage-zone-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    color: var(--tf-brand-primary, #119ae9);
}
.stage-zone-overlay-svg {
    width: 100%;
    height: 100%;
}

/*
  Hover-zoom loupe. Absolutely positioned within .stage-preview-area,
  driven by inline styles (left/top/background-*) from useHoverZoom.
  pointer-events: none keeps it a pure visual affordance -- the mouse
  events stay on the underlying <img>. Sits above the dashed-zone SVG
  overlay (z-index) but its background is the image only, so the guides
  are never magnified.
*/
.stage-zoom-lens {
    position: absolute;
    z-index: 2;
    border-radius: 10px;
    border: 2px solid var(--tf-border-strong, rgba(255, 255, 255, 0.9));
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
    background-color: var(--tf-bg-stage, #f8fafc);
    pointer-events: none;
}
</style>
